/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, createContext, useContext} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

import {useQuery} from '@tanstack/react-query'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
//import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Chakra
import {Box, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'
import {WishlistIconRefProvider} from '../../context/wishlist'
import {GigyaModalProvider} from '../../context/gigya-modal'
import {IntegrationsInitializer} from '../../context/integrations'

// Local Project Components
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import Footer from '../../components/footer'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import CheckoutFooter from '../../pages/checkout/partials/checkout-footer'
import SovraHeader from '../sovra-header'
import CorporateBenefitBanner from '../corporate-benefit-banner'
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal'
import MultisiteHeader from '../multisite-header'

// Hooks
import {AuthModal, useAuthModal} from '@salesforce/retail-react-app/app/hooks/use-auth-modal'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'
// import {useGetIntegrations} from '../../custom-api/hooks/use-get-integrations'
import useCorporateBanner from '../../hooks/use-corporate-banner'
// import useCountryBasketManagement from '../../hooks/use-country-basket-management'
import useAriaHiddenOnIframesAndDividers from '../../hooks/use-aria-hidden-iframes'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus, isServer, isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '../../utils/locale'
import Digioh from '../digioh'
//import ProfityTracker from '../profity-tracker'
import AwinMasterTag from '../awin'
import useAwinRef from '../awin/use-awin-ref'
import GlobalGigyaModal from '../modals/global-gigya-modal'

import {THEME_COLOR, DEFAULT_LOCALE, ACTIVE_DATA_ENABLED} from '../../constants'

import {Helmet} from 'react-helmet'
import useUtils from '../../custom-hooks/use-utils-helper'
import useKlarna from '../../hooks/use-klarna'
import {CustomPreferencesProvider} from '../../context/preferences'
import GtmPageView from '../gtm-page-view'
import {BRAND_LABEL, BRAND_KEY} from '../../brand'
import SnapEngage from '../snapEngage'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import DoiModal from '../modals/doi-modal'

import RecommendedPersonalizationProducts from '../recommended-personalization-products'
import SovendusLandingPageContainer from '../sovendus/sovendus-landing-page-container'
import {GlobalCustomPreferencesProvider} from '../../context/global-preferences'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMutedVideo from '../../hooks/use-muted-video'

const HeaderRefContext = createContext()

export const useHeaderRef = () => {
    return useContext(HeaderRefContext)
}

const App = ({children}) => {
    const {getTokenWhenReady} = useAccessToken()

    const activeData = useActiveData()
    const location = useLocation()
    const authModal = useAuthModal()
    const {site, locale} = useMultiSite()

    const {customParams} = getConfig()

    const {res} = useServerContext()
    const {buildLocalizedUrl, isSplashPage, localeIsPresentInUrl} = useMulticountryUtils()

    const isSplashPageCondition =
        (res?.locals?.ua?.isBot && !localeIsPresentInUrl()) || isSplashPage

    const [isOnline, setIsOnline] = useState(true)

    const {showCorporateBanner, exitSource} = useCorporateBanner()

    const styles = useStyleConfig('App')

    const headerRef = useRef(null)

    const {isComponentsLibrary, isCheckout, isCart, isPaperless, isProductDetail, isMaintenance} =
        useUtils().currentPage(location.pathname)

    const {onClose} = useDisclosure()
    const {
        isOpen: isOpenStoreLocator,
        //onOpen: onOpenStoreLocator,
        onClose: onCloseStoreLocator,
    } = useDisclosure()
    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.alias || locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n,
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname,
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale)
        },
        enabled: isServer,
    })

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency},
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email,
                },
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email, location?.pathname])

    useEffect(() => {
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    useEffect(() => {
        const handleDomContentLoaded = () => {
            // Automatically close the mobile navigation when the location path is changed.
            onClose()
        }

        if (document.readyState === 'complete') {
            handleDomContentLoaded()
        } else {
            window.addEventListener('DOMContentLoaded', handleDomContentLoaded)
        }

        return () => {
            window.removeEventListener('DOMContentLoaded', handleDomContentLoaded)
        }
    }, [location])

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()

        if (location.hash) {
            window.scrollTo(
                0,
                document.querySelector(`[name=${location.hash.replace(/#/, '')}], ${location.hash}`)
                    ?.offsetTop,
            )
        }
    }, [location])

    const {klarnaClientId, hasKlarna} = useKlarna()

    useAwinRef()
    //useGetIntegrations()

    useAriaHiddenOnIframesAndDividers()
    useMutedVideo()

    return (
        <Box className="sf-app" {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    <html lang={locale?.id} />
                    {ACTIVE_DATA_ENABLED && (
                        <script
                            src={getAssetUrl('static/head-active_data.js')}
                            id="headActiveData"
                            type="text/javascript"
                            defer
                        ></script>
                    )}

                    <title>{BRAND_LABEL}</title>
                    <meta name="theme-color" content={THEME_COLOR} />
                    <meta name="apple-mobile-web-app-title" content={BRAND_LABEL} />

                    <link
                        rel="apple-touch-icon"
                        sizes="180x180"
                        href={getAssetUrl('static/icons/apple-touch-icon.png')}
                    />
                    {/* web system icon */}
                    <link
                        rel="icon"
                        type="image/png"
                        sizes="512x512"
                        href={getAssetUrl('static/icons/app-icon-512.png')}
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        sizes="192x192"
                        href={getAssetUrl('static/icons/app-icon-192.png')}
                    />
                    <link rel="manifest" href={getAssetUrl('static/manifest.json')} />

                    {isHydrated() && hasKlarna && (
                        <script
                            async
                            src="https://eu-library.klarnaservices.com/lib.js"
                            data-client-id={klarnaClientId}
                        ></script>
                    )}

                    <link
                        rel="canonical"
                        href={`https://${customParams.currentHost}${buildLocalizedUrl(
                            location.pathname,
                        )}`}
                    />
                    <style type="text/css">{`.toggle-live-banner {display: none !important;}`}</style>
                </Helmet>
                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }
                        if (err.code === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the console error for missing translations during development,
                            // as we knew translations would be added later.
                            /*
                            logger.warn('Missing translation', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message,
                                },
                            })
                            */
                            return
                        }
                        if (locale?.alias !== 'be-by') {
                            throw err
                        }
                    }}
                    locale={targetLocale}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    {/* New Wishlist Icon Position Context Provider */}
                    <WishlistIconRefProvider>
                        <HeaderRefContext.Provider value={headerRef}>
                            <CustomPreferencesProvider>
                                <GlobalCustomPreferencesProvider>
                                    <IntegrationsInitializer>
                                        <GigyaModalProvider>
                                            <CurrencyProvider currency={currency}>
                                                <ScrollToTop />
                                                <Box
                                                    id="app"
                                                    display="flex"
                                                    flexDirection="column"
                                                    flex={1}
                                                >
                                                    {isHydrated() && <GtmPageView />}
                                                    <SkipNavLink zIndex="skipLink">
                                                        Skip to Content
                                                    </SkipNavLink>
                                                    <StoreLocatorModal
                                                        isOpen={isOpenStoreLocator}
                                                        onClose={onCloseStoreLocator}
                                                    />
                                                    {!isCheckout &&
                                                        !isComponentsLibrary &&
                                                        !isMaintenance &&
                                                        !isSplashPageCondition &&
                                                        (showCorporateBanner ? (
                                                            <CorporateBenefitBanner
                                                                onExit={exitSource}
                                                            />
                                                        ) : (
                                                            <SovraHeader />
                                                        ))}

                                                    <Box
                                                        {...styles.headerWrapper}
                                                        position={
                                                            isProductDetail || isPaperless
                                                                ? 'relative'
                                                                : 'sticky'
                                                        }
                                                        ref={headerRef}
                                                    >
                                                        {!isMaintenance &&
                                                        !isSplashPageCondition ? (
                                                            !isComponentsLibrary ? (
                                                                !isCheckout ? (
                                                                    <MultisiteHeader />
                                                                ) : (
                                                                    <CheckoutHeader />
                                                                )
                                                            ) : null
                                                        ) : null}
                                                    </Box>
                                                    {!isOnline && <OfflineBanner />}
                                                    <AddToCartModalProvider>
                                                        <SkipNavContent
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                flex: 1,
                                                                outline: 0,
                                                            }}
                                                        >
                                                            <Box
                                                                as="main"
                                                                id="app-main"
                                                                role="main"
                                                                display="flex"
                                                                flexDirection="column"
                                                                flex="1"
                                                                position="relative"
                                                            >
                                                                <RecommendedPersonalizationProducts />
                                                                <OfflineBoundary isOnline={false}>
                                                                    {children}
                                                                </OfflineBoundary>
                                                            </Box>
                                                        </SkipNavContent>
                                                        {!isComponentsLibrary &&
                                                        !isSplashPageCondition ? (
                                                            isCart ||
                                                            isCheckout ||
                                                            isMaintenance ? (
                                                                <CheckoutFooter />
                                                            ) : (
                                                                <>
                                                                    <Footer />
                                                                </>
                                                            )
                                                        ) : (
                                                            <></>
                                                        )}

                                                        {/* TODO: Empty div to prevent a console errpr. Remove when go live. */}
                                                        {BRAND_KEY === 'kenwood' && (
                                                            <div id="js-gigya-login-popup-container" />
                                                        )}

                                                        <AuthModal {...authModal} />
                                                    </AddToCartModalProvider>
                                                </Box>
                                                <Digioh />
                                                {/* <ProfityTracker /> */}
                                                <SovendusLandingPageContainer />
                                                <AwinMasterTag />
                                                <SnapEngage />
                                                <GlobalGigyaModal />
                                                <DoiModal />
                                            </CurrencyProvider>
                                        </GigyaModalProvider>
                                    </IntegrationsInitializer>
                                </GlobalCustomPreferencesProvider>
                            </CustomPreferencesProvider>
                        </HeaderRefContext.Provider>
                    </WishlistIconRefProvider>
                </IntlProvider>
                {ACTIVE_DATA_ENABLED && (
                    <script
                        type="text/javascript"
                        src={getAssetUrl('static/dwanalytics-22.2.js')}
                        id="dwanalytics"
                        async="async"
                        onLoad={trackPage}
                    ></script>
                )}
                {ACTIVE_DATA_ENABLED && (
                    <script
                        src={getAssetUrl('static/dwac-21.7.js')}
                        type="text/javascript"
                        id="dwac"
                        async="async"
                    ></script>
                )}
            </StorefrontPreview>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node,
}

export default App
